import {Injectable} from "@angular/core";
import {CustomerInstanceVenueService} from "./customer-instance-venue.service";
import {CustomerInstanceVenueViewData} from "./customer-instance-venue-view.data";
import {CustomerInstanceVenueData} from "./customer-instance-venue.data";
import {VenuePlanData} from "./venue-plan/venue-plan.data";
import {VenueContactData} from "./venue-contact/venue-contact.data";
import {VenueAddress} from "./venue-address/venue-address.data";
import {VenueServices} from "./venue-services/venue-services.data";
import {VenueCompany} from "./venue-company/venue-company.data";
import {VenueCardVerification} from "./venue-card-verification/venue-card-verification.data";
import {UserData} from "../auth/user.data";
import {BehaviorSubject, Subject} from "rxjs";

@Injectable()
export class CustomerInstanceVenueModel {

    data: CustomerInstanceVenueViewData = new CustomerInstanceVenueViewData();
    data$: Subject<CustomerInstanceVenueViewData> = new BehaviorSubject(undefined);

    private _salesRep: boolean;
    roleInitialised: boolean;

    loggedUser: UserData;

    constructor(private customerInstanceVenueService: CustomerInstanceVenueService) {
    }

    async new(): Promise<void> {
        this.data = new CustomerInstanceVenueViewData();
        this.data.venue = new CustomerInstanceVenueData();
        this.data.venue.draft = true;
        this.initEntity(this.data);

        this.data$.next(this.data);
    }

    async load(venueId: string): Promise<void> {
        this.data = await this.customerInstanceVenueService.getCustomerInstanceVenue(venueId);
        this.initEntity(this.data);
        this.data$.next(this.data);
    }

    initEntity(data: CustomerInstanceVenueViewData): void {
        if (!data.venuePlan) {
            data.venuePlan = new VenuePlanData();
        }
        if (!data.primaryContact) {
            data.primaryContact = new VenueContactData();
            data.primaryContact.id = VenueContactData.PRIMARY_CONTACT_ID;
        }
        if (!data.siteContact) {
            data.siteContact = new VenueContactData();
            data.siteContact.id = VenueContactData.PRIMARY_SITE_ID;
        }
        if (!data.address) {
            data.address = new VenueAddress();
            data.address.addressState = "QLD";
        }
        if (!data.services) {
            data.services = new VenueServices();
            data.services.tableService = true;
        }
        if (!data.company) {
            data.company = new VenueCompany();
        }
    }

    async refresh(): Promise<void> {
        await this.load(this.data.venue.id);
    }

    get venueId(): string {
        return this.data.venue ? this.data.venue.id : undefined;
    }

    get contractComplete(): boolean {
        return this.data.contract && (this.data.contract.uploadedContract ||
            (this.data.contract.signedContract && (this.data.contract.signedContract.status === "document.completed" ||
                this.data.contract.signedContract.status === "signed")));
    }

    get signUpComplete(): boolean {
        return (this.data && this.data.venue && this.data.venue.deploymentStatus === CustomerInstanceVenueData.DEPLOYMENT_STATUS_READY) ||
            this.contractComplete && this.billingComplete;
    }

    get billingComplete(): boolean {
        return this.data.billingAccount && !!this.data.billingAccount.paymentBankAccount;
    }

    get uploadedContract(): boolean {
        return this.data.contract && this.data.contract.uploadedContract;
    }

    get contractRejected(): boolean {
        if (!this.data.contract || !this.data.contract.signedContract) {
            return false;
        }

        return this.data.contract.signedContract.status === "document.declined"
            || this.data.contract.signedContract.status === "document.voided"
            || this.data.contract.signedContract.status === "document.rejected"
            || this.data.contract.signedContract.status === "withdrawn";
    }

    async saveCompany(venueCompany: VenueCompany): Promise<void> {
        this.data.company = await this.customerInstanceVenueService.saveVenueCompany(this.venueId, venueCompany);
    }

    async saveAddress(venueAddress: VenueAddress): Promise<void> {
        this.data.address = await this.customerInstanceVenueService.saveVenueAddress(this.venueId, venueAddress);
    }

    async saveCardVerification(cardVerification: VenueCardVerification): Promise<void> {
        this.data.cardVerification = await this.customerInstanceVenueService.saveVenueCardVerification(this.venueId, cardVerification);
    }

    async saveContact(venueContact: VenueContactData): Promise<void> {
        if (venueContact.id === VenueContactData.PRIMARY_SITE_ID) {
            this.data.siteContact = await this.customerInstanceVenueService.saveVenueContact(this.venueId, venueContact);
        } else {
            this.data.primaryContact = await this.customerInstanceVenueService.saveVenueContact(this.venueId, venueContact);
        }
    }

    async saveServices(venueServices: VenueServices): Promise<void> {
        this.data.services = await this.customerInstanceVenueService.saveVenueServices(this.venueId, venueServices);
    }

    async updatePlan(venuePlan: VenuePlanData): Promise<void> {
        this.data.venuePlan = await this.customerInstanceVenueService.updatePlan(this.venueId, venuePlan);
        this.data$.next(this.data);
    }

    async updatePlanManagement(venuePlan: VenuePlanData): Promise<void> {
        this.data.venuePlan = await this.customerInstanceVenueService.updatePlanManagement(this.venueId, venuePlan);
        this.data$.next(this.data);
    }

    get draft(): boolean {
        return this.data && this.data.venue && this.data.venue.draft;
    }

    /**
     * Indicates if the inline edit control should be available
     */
    canEdit(allowChurn: boolean = false): boolean {
        if (!this.roleInitialised) {
            return false;
        }

        if (this._salesRep) {
            return false;
        }

        if (UserData.isSalesManager(this.loggedUser)) {
            return false;
        }

        return !this.draft && this.data && this.data.venue && (this.data.venue.deploymentStatus === CustomerInstanceVenueData.DEPLOYMENT_STATUS_READY || (allowChurn && this.data.venue.deploymentStatus === CustomerInstanceVenueData.DEPLOYMENT_STATUS_CHURNED));
    }

    get newVenue(): boolean {
        return !this.venueId;
    }

    set salesRep(rep: boolean) {
        this._salesRep = rep;
        this.roleInitialised = true;
    }

    get appPaymentAccountVerified(): boolean {
        return this.data.appPaymentAccount && this.data.appPaymentAccount.accountVerified;
    }
}
